/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import { Icon, IconColor, IconName } from 'common/components/controls';
// eslint-disable-next-line no-restricted-imports
import { Noun, Verb } from 'common/debug/analytics-constants';
// eslint-disable-next-line no-restricted-imports
import HeaderLink from 'components/header/header-link';
// eslint-disable-next-line no-restricted-imports
import { SearchResultsPopoverId } from 'components/popovers/popover-ids';
// eslint-disable-next-line no-restricted-imports
import { ScreenBreakpoints } from 'components/responsive';
// eslint-disable-next-line no-restricted-imports
import Keybindings, { Key } from 'components/shared/keybindings';
// eslint-disable-next-line no-restricted-imports
import RouterLink from 'common/components/singlespa/router-link';
// eslint-disable-next-line no-restricted-imports
import Analytics from 'debug/analytics';
// eslint-disable-next-line no-restricted-imports
import {
  clearSearchQuery,
  closePopover,
  closePopovers,
  getSearchSuggestions,
  hideAddSavedSearchForm,
  hideSavedSearchPromo,
  performSearch,
  setSearchQuery,
  showPopover,
  State,
} from 'modules';
import React, { lazy, Suspense } from 'react';
import Media from 'react-media';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
// eslint-disable-next-line no-restricted-imports
import {
  areAnyPopoversOrOverlaysCurrentlyOpen,
  getCurrentSearchQuery,
  isPopoverShown,
} from 'selectors';
// eslint-disable-next-line no-restricted-imports
import dynamicDebounce from 'util/dynamic-debounce';
// eslint-disable-next-line no-restricted-imports
import randomToken from 'util/random-token';
import styles from './form.less';

const SearchResultsPopoverPromise = import(/* webpackChunkName: "gamma-search-results-popover" */ 'components/popovers/search-results');
const SearchResultsPopover = lazy(() => SearchResultsPopoverPromise);

const inputId = randomToken(32);

interface FormProps {
  isActive: boolean;
  isAnyPopoverOpen: boolean;
  currentQuery: string;
  onInputBlur: () => void;
  onInputClick: React.MouseEventHandler<HTMLInputElement>;
  onResetSearch: () => void;
  onSearch: (query: string) => void;
  onSuggest: (query: string, caretPos: number) => void;
  onShortcutFocus: () => void;
  onSetSearchQuery: (query: string) => void;
}

interface AllProps extends FormProps {}

class SearchForm extends React.Component<AllProps> {
  inputRef = React.createRef<HTMLInputElement>();

  private getSearchDelay = () => {
    const { currentQuery } = this.props;
    if (currentQuery.length <= 1) {
      return 1800;
    } else if (currentQuery.length <= 3) {
      return 1200;
    }

    return 600;
  };

  private debouncedSearch = dynamicDebounce(
    this.props.onSearch,
    this.getSearchDelay,
  );

  private debouncedSuggest = dynamicDebounce(this.props.onSuggest, 100);

  componentDidMount() {
    const { currentQuery, onSetSearchQuery } = this.props;

    if (currentQuery !== '') {
      onSetSearchQuery('');
    }
    document.addEventListener('click', this.stopClickPropagation, true);
  }

  componentDidUpdate(prevProps: FormProps) {
    // This is for when `this.props.onInputBlur` gets called without a MouseEvent
    // (namely when the user presses `Esc`). Without this, the popover closes,
    // but the input remains in the active state
    if (this.inputRef.current && !this.props.isActive && prevProps.isActive) {
      this.inputRef.current.blur();
    }
    // Also needed for focus without mouse event
    if (
      this.inputRef.current &&
      this.props.isActive &&
      (!prevProps.isActive ||
        this.props.currentQuery === '' ||
        this.props.currentQuery !== prevProps.currentQuery)
    ) {
      this.inputRef.current.focus();
    }
  }

  componentWillUnmount() {
    this.debouncedSearch.clear();
    document.removeEventListener('click', this.stopClickPropagation, true);
  }

  /**
   * Because the search results popover is shown/hidden in response to the
   * focus and blur events and because those events are dispatched before
   * click events, we need to call preventDefault() to keep the popover from
   * closing itself when the input is clicked.
   */
  stopClickPropagation = (e: MouseEvent) => {
    const inputNode = this.inputRef.current;
    if (e.target === inputNode) {
      e.preventDefault();
    }
  };

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.currentTarget.value;
    const { current } = this.inputRef;
    this.props.onSetSearchQuery(query);
    if (query.length) {
      this.debouncedSearch(query);
      this.debouncedSuggest(
        query,
        current && current.selectionStart ? current.selectionStart : 0,
      );
    }
  };

  onBlur = () => {
    // prevents calling this twice when pressing escape
    if (this.props.isActive) {
      this.props.onInputBlur();
    }
  };

  onShortcut = (e: KeyboardEvent) => {
    const { isAnyPopoverOpen, onShortcutFocus } = this.props;
    if (!isAnyPopoverOpen) {
      e.preventDefault();
      onShortcutFocus();
    }
  };

  keybindings = {
    [Key.Escape]: {
      callback: this.onBlur,
      stopPropagation: true,
      preventDefault: true,
    },
    [Key.ForwardSlash]: {
      callback: this.onShortcut,
      isGlobal: true,
    },
  };

  render() {
    const {
      currentQuery,
      isActive = false,
      onInputClick,
      onShortcutFocus,
    } = this.props;

    return (
      <>
        {isActive && (
          <Suspense fallback={null}>
            <SearchResultsPopover
              inputRef={this.inputRef}
              onClose={this.onBlur}
              target={() => this.inputRef.current}
            />
          </Suspense>
        )}
        <Media query={ScreenBreakpoints.Small}>
          {(matches: boolean) =>
            matches ? (
              <HeaderLink href="/search" buttonStyle="button">
                <Icon name={IconName.Search} color={IconColor.Light} large />
              </HeaderLink>
            ) : (
              <Keybindings keybindings={this.keybindings}>
                <div
                  className={classNames([
                    styles.search,
                    isActive ? styles.active : '',
                  ])}
                >
                  <input
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className={styles.searchInput}
                    data-test-id={HeaderTestIds.SearchInput}
                    id={inputId}
                    onClick={onInputClick}
                    onFocus={onShortcutFocus}
                    onChange={this.onChange}
                    ref={this.inputRef}
                    type="search"
                    value={currentQuery}
                  />
                  <span className={styles.buttons}>
                    {isActive ? (
                      <>
                        <RouterLink
                          href={`/search?q=${encodeURIComponent(currentQuery)}`}
                        >
                          <Icon
                            className={classNames([
                              styles.button,
                              styles.externalIconButton,
                            ])}
                            large
                            name={IconName.ExternalLink}
                          />
                        </RouterLink>
                        <Icon
                          className={styles.button}
                          large
                          name={IconName.Close}
                          testId={HeaderTestIds.SearchClose}
                        />
                      </>
                    ) : (
                      <label htmlFor={inputId}>
                        <Icon
                          className={styles.button}
                          large
                          name={IconName.Search}
                        />
                      </label>
                    )}
                  </span>
                </div>
              </Keybindings>
            )
          }
        </Media>
      </>
    );
  }
}

const mapStateToProps = (state: State) => ({
  isActive: isPopoverShown(state, SearchResultsPopoverId),
  isAnyPopoverOpen: areAnyPopoversOrOverlaysCurrentlyOpen(state),
  currentQuery: getCurrentSearchQuery(state),
});

const mapDispatchToProps = (dispatch: Dispatch<object>) => ({
  onInputClick(e: React.MouseEvent<HTMLInputElement>) {
    Analytics.trackUE({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.SEARCH_INPUT,
      method: 'by clicking on search input',
    });
  },

  onSearch(query: string) {
    dispatch(performSearch({}));
  },

  onSuggest(query: string, caretPos: number) {
    dispatch(getSearchSuggestions(caretPos));
  },

  onSetSearchQuery(query: string) {
    dispatch(setSearchQuery({ query }));
  },

  onInputBlur() {
    dispatch(closePopover(SearchResultsPopoverId));
    dispatch(clearSearchQuery());
    dispatch(hideAddSavedSearchForm());
    dispatch(hideSavedSearchPromo());

    Analytics.trackUE({
      category: Noun.HEADER,
      verb: Verb.CLOSES,
      directObj: Noun.SEARCH_INPUT,
    });
  },

  onShortcutFocus() {
    dispatch(closePopovers());
    dispatch(showPopover({ name: SearchResultsPopoverId }));

    Analytics.trackUE({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.SEARCH_INPUT,
      method: 'by using search shortcut',
    });
  },
  onResetSearch() {
    dispatch(clearSearchQuery());
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SearchForm);



// WEBPACK FOOTER //
// ./app/gamma/src/components/search/form.tsx